import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '@sales-center/components'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { IMoreMenuItem, AlertDialogSlideProps } from '@sales-center/components'
import { permissionDenied } from '@user/store/authorization/permissions'
import { useDialogState } from '@user/utils/useDialogState'
import { LeadParentHeaderProps } from '../typings'
import { deleteLead, clearLeadDetail } from '@user/store/lead'

type Result = AlertDialogSlideProps & {
  deleteMenuItem: IMoreMenuItem
}

export function useDeleteLeadMenuItem(params: LeadParentHeaderProps): Result {
  const {
    canDelete,
    detailPage,
    lead: { id },
  } = params
  const [deleteLoading, setDeleteLoading] = useState(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [open, onOpen, cancelClick] = useDialogState()

  const okClick = useCallback(async () => {
    setDeleteLoading(true)
    const result = await dispatch(deleteLead(id))

    if (deleteLead.fulfilled.match(result)) {
      setDeleteLoading(false)
      toast.success('Lead deleted.')

      if (detailPage) {
        navigate('/leads')
        dispatch(clearLeadDetail())
      }
    } else {
      setDeleteLoading(false)
      toast.error('Failed to delete lead.')
    }
  }, [detailPage, dispatch, navigate, id])

  const deleteMenuItem = useMemo<IMoreMenuItem>(
    () => ({
      label: 'Delete',
      onClick: onOpen,
      disabled: !canDelete,
      dataTestId: `deleteBtn-${id}`,
      tooltipText: canDelete ? undefined : permissionDenied,
    }),
    [canDelete, onOpen],
  )

  return {
    title: 'Delete Lead',
    message: 'Are you sure you want to delete this Lead?',
    okButtonText: 'Delete',
    open,
    cancelClick,
    okClick,
    deleteMenuItem,
    okButtonDisabled: deleteLoading,
  }
}
